<template>
  <form>
    <div class="mb-3">
      <span v-if="node && node.name">
        {{ parentNodeTitle }}
      </span>
    </div>
    <VAutocomplete
      ref="autocompleteRef"
      v-model="selectedAnalysisTypes"
      :label="titleTranslate()"
      :items="displayedAnalysisTypes"
      :multiple="true"
      :clearable="true"
      :loading="loading"
      :itemValue="itemValue"
      outlined
      data-cy="addAnalysis-analysisType-select"
      @input="updateData"
    />
  </form>
</template>

<script>
import { mapGetters } from 'vuex';
import VAutocomplete from '@/components/shared/widgets/forms/VuetifyInputs/VAutocomplete.vue';
import {
    PRODUCT_CONSUMPTION_ANALYSIS_TYPE,
    WATER_CONSUMPTION_ANALYSIS_TYPE,
    SIMPLE_ANALYSIS_TYPE,
    FUEL_CONSUMPTION_ANALYSIS_TYPE,
    CALCULATED_ANALYSIS_TYPE,
} from '@/aquassist-shared/constants/types/componentAnalysisConfiguration';
import addAnalysisFormTitle from '@/constants/addAnalysisFormTitle';
import { COOLING } from '@/aquassist-shared/constants/types/components';
import { TYPE_MAKE_UP, TYPE_BLOWDOWN } from '@/aquassist-shared/constants/types/flowMeters';
import lockService from '@/utilities/lockService';

export default {
    components: {
        VAutocomplete,
    },
    props: {
        node: {
            type: Object,
            default: () => ({}),
        },
        compTitle: {
            type: String,
            default: '',
        },
        actionName: {
            type: String,
            default: '',
        },
        itemValue: {
            type: String,
            default: 'id',
        },
        selectedAnalysis: {
            type: Array,
            default: null,
        },
        isFromSiteTree: {
            type: Boolean,
            default: false,
        },
        type: {
            type: Array,
            default: () => [WATER_CONSUMPTION_ANALYSIS_TYPE, PRODUCT_CONSUMPTION_ANALYSIS_TYPE],
        },
    },
    data() {
        return {
            loading: false,
            analysisTypes: [],
            configurations: [],
            queryParams: { pagination: false },
            selectedAnalysisTypes: [],
            firstLoadSelectedAnalysis: [],
            firstLoad: true,
            currentOperation: '',
        };
    },
    computed: {
        ...mapGetters([
            'userLang',
            'teamMainLang',
        ]),
        parentNodeTitle() {
            const lowerCaseNodeType = this.$options.filters.lowercase(this.node.nodeType);
            return `${this.$t('sites.nodes.parentNode')}: ${this.node.name} (${this.$t(`sites.nodes.${lowerCaseNodeType}`)})`;
        },
        /**
         * Returns an array of displayed analysis types.
         *
         * @returns {Array} An array of displayed analysis types to be shown in the autocomplete.
         * We filter out the water consumption analysis type.
         */
        displayedAnalysisTypes() {
            const analysisTypes = this.analysisTypes.filter(analysis => {
                if (this.type.length === 1) {
                    /* For adding flowmeter to Cooling component we need  Water consumption analyses has just
                    flowmeter type  Make-up and Blowdown analysis */
                    if (this.type.includes(WATER_CONSUMPTION_ANALYSIS_TYPE) && this.node?.type?.key === COOLING) {
                        return this.type.includes(analysis.type) && [TYPE_MAKE_UP, TYPE_BLOWDOWN].includes(analysis.flowmeterType);
                    }
                    return this.type.includes(analysis.type);
                }
                return !this.type.includes(analysis.type);
            });
            return analysisTypes.map(type => ({
                name: type.name[this.userLang] || type.name[this.teamMainLang],
                id: type.id,
                key: type.key,
            }));
        },
    },
    mounted() {
        window.addEventListener('beforeunload', this.handleUnload);
    },

    beforeDestroy() {
        if (this.node && this.node.ref) {
            lockService.releaseLock(this.node.ref, this.actionName);
        }
        window.removeEventListener('beforeunload', this.handleUnload);
    },
    async created() {
        this.loading = true;
        await this.getAnalysisTypes();
        await this.getComponentConfigurations();
        await this.getAnalysisTypeSelected();
        if (this.firstLoad) {
            // Only scenario we enter this condition is if component is being created from SiteTree.vue
            if (!this.selectedAnalysis && this.isFromSiteTree) {
                this.selectedAnalysisTypes = this.configurations
                    .filter(item => item.active)
                    .map(item => item.analysisType.id);
            }
            this.firstLoadSelectedAnalysis = [...this.selectedAnalysisTypes];
            this.firstLoad = false;
        }
        this.loading = false;
    },
    methods: {
        async getAnalysisTypes() {
            const { data } = await this.$rq.getAnalysisTypes(this.queryParams);
            this.analysisTypes = [...this.analysisTypes, ...data.types];
        },
        handleUnload() {
            if (this.node && this.node.ref) {
                lockService.releaseLock(this.node.ref, this.actionName);
            }
        },
        async getComponentConfigurations() {
            if (this.node && !this.node.alreadyUsedAnalysisTypes) {
                const { data } = await this.$rq.getComponentConfigurations(this.node.ref);
                if (this.compTitle === 'editAnalysis') {
                    this.configurations = data.configurations.filter(item => item.analysisType.type === SIMPLE_ANALYSIS_TYPE
                        || item.analysisType.type === CALCULATED_ANALYSIS_TYPE);
                }
                if (this.compTitle === 'addFlowmeter') {
                    this.configurations = data?.configurations.filter(item => item.analysisType.type === WATER_CONSUMPTION_ANALYSIS_TYPE);
                }
                if (this.compTitle === 'addProductLevel') {
                    this.configurations = data?.configurations.filter(item => item.analysisType.type === PRODUCT_CONSUMPTION_ANALYSIS_TYPE);
                }
                if (this.compTitle === 'addFuelConsumption') {
                    this.configurations = data?.configurations.filter(item => item.analysisType.type === FUEL_CONSUMPTION_ANALYSIS_TYPE);
                }
            } else {
                this.configurations = this.node.alreadyUsedAnalysisTypes;
            }
        },
        titleTranslate() {
            if (this.compTitle === addAnalysisFormTitle.editAnalysis) return this.$t('forms.labels.analysisType');
            if (this.compTitle === addAnalysisFormTitle.addFlowmeter) return this.$t('devices.flowMeters.name');
            if (this.compTitle === addAnalysisFormTitle.addProductLevel) return this.$t('devices.productLevel.name');
            return this.$t('forms.labels.analysisType');
        },
        updateData(select) {
            this.$emit('sendData', {
                // we get the componentId from the clicked node
                componentId: this.node ? this.node.ref : null,
                analyses: select,
                siteId: this.node ? this.node.siteId : null,
                firstLoadSelectedAnalysis: this.firstLoadSelectedAnalysis,
                configurations: this.configurations,
            });
        },
        getAnalysisTypeSelected() {
            if (this.node.alreadyUsedAnalysisTypes) {
                this.selectedAnalysisTypes = this.node.alreadyUsedAnalysisTypes.map(item => item.analysisType.id);
            } else if (this.selectedAnalysis) {
                this.selectedAnalysisTypes = this.selectedAnalysis;
            }
        },
    },
};
</script>
